import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { Icon, type IconInput } from '../icon/icon';

/**
 * Search field — Figma's `Search Input` (node 90:17760), the last component set on
 * the Input page.
 *
 * One size, four states: Default / Focus / Filled / Disabled, plus two boolean
 * slot properties (`leadIcon`, `tailIcon`) that appear in no variant name. It is
 * **not** a variant of `<ui-input>` — the pill radius (16 vs 8), the 16px icons
 * (vs 20), the fixed 40px height and the absence of label/helper are all its own.
 *
 * As with `<ui-input>`, the control is **projected, not generated**, so
 * `formControlName`, `type`, `autocomplete` and validation stay with the platform,
 * and `State` is not an input — focus, filled and disabled are read from
 * `:focus-within`, `:placeholder-shown` and `:disabled`:
 *
 * ```html
 * <ui-search-input>
 *   <input uiInput type="search" placeholder="Search" aria-label="Search posts" />
 * </ui-search-input>
 * ```
 *
 * The projected control needs its own accessible name — there is no label in this
 * design, and a placeholder is not one. Give it `aria-label`, or point
 * `aria-labelledby` at visible text.
 *
 * A `placeholder` is also what tells the field it is empty: the resting-vs-filled
 * colour shift keys off `:placeholder-shown`, which never matches a control that
 * has no placeholder at all.
 */
@Component({
  selector: 'ui-search-input',
  imports: [Icon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './search-input.scss',
  host: { class: 'ui-search-input' },
  template: `
    <div class="ui-search-input__field">
      @if (leadIcon()) {
        <ui-icon class="ui-search-input__lead-icon" [name]="leadIcon()!" [strokeWidth]="1.5" />
      }

      <span class="ui-search-input__control"><ng-content /></span>

      @if (tailIcon()) {
        <ui-icon class="ui-search-input__tail-icon" [name]="tailIcon()!" [strokeWidth]="1.5" />
      }
    </div>
  `,
})
export class SearchInput {
  /** Figma's `leadIcon` slot — a magnifier. Pass `undefined` to drop it. */
  readonly leadIcon = input<IconInput | undefined>('search');

  /**
   * Figma's `tailIcon` slot — an `info` glyph. Decorative, and announced to nobody;
   * pass `undefined` to drop it, or a different glyph to replace it. It is not a
   * clear/reset control — Figma draws no action there (see
   * docs/design-system-feedback.md #41).
   */
  readonly tailIcon = input<IconInput | undefined>('info');
}
